export const firstPresent = (...values) => values.find((value) => {
  if (value === 0) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).trim() !== '';
});

const asRecord = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const parseStringContainer = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;

  if ((text.startsWith('[') && text.endsWith(']')) || (text.startsWith('{') && text.endsWith('}'))) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  return null;
};

const flattenImages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenImages);

  if (typeof value === 'string') {
    const parsed = parseStringContainer(value);
    if (parsed) return flattenImages(parsed);

    const normalized = value.replace(/&amp;/gi, '&').trim();
    return normalized ? [normalized] : [];
  }

  if (typeof value === 'object') {
    const direct = firstPresent(
      value.downloadURL,
      value.downloadUrl,
      value.url,
      value.src,
      value.path,
      value.fullPath,
      value.value,
    );

    if (direct) return flattenImages(direct);

    // Supports Firestore maps such as {0: urlA, 1: urlB}.
    return Object.values(value).flatMap(flattenImages);
  }

  return [];
};

export const getProjectImages = (project) => {
  const safeProject = asRecord(project);
  const candidates = [
    safeProject.demand_cover_photo,
    safeProject.project_cover_image,
    safeProject.cover_image,
    safeProject.coverImage,
    safeProject.demand_image,
    safeProject.demandImage,
    safeProject.demand_images,
    safeProject.images,
    safeProject.photos,
    safeProject.image_path,
  ];

  return [...new Set(candidates.flatMap(flattenImages).filter(Boolean))];
};

export const getProjectDescription = (project) => {
  const safeProject = asRecord(project);
  return firstPresent(
    safeProject.description,
    safeProject.demand,
    safeProject.demand_description,
    safeProject.title,
    safeProject.contract?.description,
    'Projet réalisé',
  );
};

export const getProjectRating = (project) => {
  const safeProject = asRecord(project);
  return Number(firstPresent(
    safeProject.review?.rating,
    safeProject.rating,
    safeProject.artisan_rating,
    0,
  )) || 0;
};

export const getArtisanName = (project) => {
  const safeProject = asRecord(project);
  const source = asRecord(safeProject.contract || safeProject.estimation);
  const firstName = firstPresent(
    source.artisan_firstname,
    source.artisan_first_name,
    source.artisanFirstName,
    safeProject.artisan_firstname,
  );
  const lastName = firstPresent(
    source.artisan_lastname,
    source.artisan_last_name,
    source.artisanLastName,
    safeProject.artisan_lastname,
  );
  return [firstName, lastName].filter(Boolean).join(' ') || 'Artisan';
};

export const getClientName = (record) => {
  const safeRecord = asRecord(record);
  const firstName = firstPresent(
    safeRecord.user_firstname,
    safeRecord.user_first_name,
    safeRecord.firstName,
    safeRecord.first_name,
    safeRecord.client_firstname,
    safeRecord.client_first_name,
  );
  const lastName = firstPresent(
    safeRecord.user_lastname,
    safeRecord.user_last_name,
    safeRecord.lastName,
    safeRecord.last_name,
    safeRecord.client_lastname,
    safeRecord.client_last_name,
  );
  return [firstName, lastName].filter(Boolean).join(' ') || 'Client';
};

export const getProfileImage = (profile) => {
  const safeProfile = asRecord(profile);
  return firstPresent(
    safeProfile.profile_image,
    safeProfile.profileImage,
    safeProfile.photoURL,
    safeProfile.photo_url,
    safeProfile.client_profile_image,
    safeProfile.artisan_profile_image,
    safeProfile.image_path,
    '',
  );
};

export const getProfileName = (profile) => {
  const safeProfile = asRecord(profile);
  const firstName = firstPresent(
    safeProfile.firstName,
    safeProfile.first_name,
    safeProfile.user_firstname,
  );
  const lastName = firstPresent(
    safeProfile.lastName,
    safeProfile.last_name,
    safeProfile.user_lastname,
  );
  return [firstName, lastName].filter(Boolean).join(' ') || 'Votre profil';
};
